import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, Close, DeleteOutline, Edit, Visibility } from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import CatalogWizardModal from "./components/CatalogWizardModal";
import { KoraxPageHeader } from "../../components/KoraxUI";

const currency = value =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const useStyles = makeStyles(theme => ({
  pageRoot: {
    width: "100%",
    maxWidth: "100%",
    minHeight: "calc(100vh - 56px)",
    padding: "24px 14px 64px",
    paddingBottom: theme.spacing(8),
    boxSizing: "border-box",
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    background: "linear-gradient(145deg,#EEF5FF 0%,#F8FAFC 100%)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      padding: "18px 10px 72px",
      paddingBottom: theme.spacing(9)
    }
  },
  hero: {
    borderRadius: 28,
    padding: theme.spacing(2.5),
    background: "linear-gradient(135deg,#FFFFFF 0%,#EAF3FF 55%,#F8FAFC 100%)",
    border: "1px solid #E2E8F0",
    boxShadow: "0 22px 55px rgba(0,102,255,.11)",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      borderRadius: 22,
      padding: theme.spacing(1.6)
    }
  },
  heroLeft: { display: "flex", alignItems: "center", gap: theme.spacing(1.4), minWidth: 0 },
  heroIcon: {
    width: 60,
    height: 60,
    borderRadius: 20,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    color: "#fff",
    boxShadow: "0 10px 24px rgba(0,102,255,.28)",
    flexShrink: 0
  },
  heroTitle: { fontWeight: 950, fontSize: "1.7rem", color: "#0F172A", lineHeight: 1.05 },
  heroSubtitle: { fontSize: ".86rem", color: "#64748B", marginTop: 8, lineHeight: 1.45 },
  addBtn: {
    borderRadius: 16,
    fontWeight: 900,
    textTransform: "none",
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    padding: theme.spacing(1.1, 2),
    boxShadow: "0 12px 26px rgba(0,102,255,.22)",
    "&:hover": { background: "linear-gradient(135deg,#0052CC,#007ACC)" },
    [theme.breakpoints.down("sm")]: { width: "100%" }
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 360px",
    gap: theme.spacing(1.5),
    alignItems: "start",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" }
  },
  mainCard: {
    borderRadius: 28,
    background: "#fff",
    border: "1px solid #E2E8F0",
    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
    overflow: "hidden"
  },
  toolbar: {
    padding: theme.spacing(1.4),
    borderBottom: "1px solid #E2E8F0",
    display: "grid",
    gridTemplateColumns: "1fr 170px 170px auto",
    gap: theme.spacing(1),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 16,
      background: "#F8FAFC"
    }
  },
  tabs: {
    display: "flex",
    gap: theme.spacing(.8),
    padding: theme.spacing(1.2, 1.4, 0),
    flexWrap: "wrap"
  },
  tabBtn: {
    borderRadius: 999,
    border: "1px solid #E2E8F0",
    background: "#fff",
    padding: "9px 15px",
    cursor: "pointer",
    fontWeight: 900,
    color: "#475569"
  },
  tabActive: {
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    borderColor: "transparent",
    boxShadow: "0 8px 18px rgba(0,102,255,.20)"
  },
  itemsList: { padding: theme.spacing(1.2), display: "grid", gap: theme.spacing(1) },
  catalogGrid: {
    padding: theme.spacing(1.2),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: theme.spacing(1.2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  productCard: {
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    boxShadow: "0 16px 34px rgba(15,23,42,.07)",
    transition: "all .18s",
    position: "relative",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 24px 48px rgba(15,23,42,.10)",
      borderColor: "rgba(0,102,255,.25)"
    }
  },
  productImage: {
    height: 170,
    background: "linear-gradient(135deg,#EEF5FF,#FFFFFF)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 42,
    color: "#0066FF",
    overflow: "hidden",
    position: "relative"
  },
  productImageReal: {
    width: "100%",
    height: "100%",
    objectFit: "cover"
  },
  productBadge: {
    position: "absolute",
    top: 12,
    left: 12,
    borderRadius: 999,
    padding: "6px 10px",
    background: "rgba(255,255,255,.92)",
    backdropFilter: "blur(8px)",
    fontSize: ".68rem",
    fontWeight: 950,
    color: "#0066FF",
    border: "1px solid rgba(0,102,255,.14)"
  },
  productBody: {
    padding: theme.spacing(1.4)
  },
  productTitle: {
    fontWeight: 950,
    color: "#0F172A",
    fontSize: "1rem",
    lineHeight: 1.2,
    minHeight: 38
  },
  productDescription: {
    color: "#64748B",
    fontSize: ".75rem",
    marginTop: 7,
    lineHeight: 1.4,
    minHeight: 38,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
  },
  productFooter: {
    marginTop: theme.spacing(1.2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  cardActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(.8),
    marginTop: theme.spacing(1.2)
  },
  cardActionBtn: {
    borderRadius: 14,
    border: "1px solid #E2E8F0",
    background: "#F8FAFC",
    cursor: "pointer",
    padding: "9px 8px",
    fontWeight: 900,
    color: "#334155"
  },
  previewShell: {
    borderRadius: 30,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 24px 80px rgba(15,23,42,.22)"
  },
  previewHero: {
    height: 260,
    background: "linear-gradient(135deg,#EEF5FF,#FFFFFF)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 58,
    color: "#0066FF",
    position: "relative",
    overflow: "hidden"
  },
  previewContent: {
    padding: theme.spacing(2)
  },
  previewTitle: {
    fontWeight: 950,
    fontSize: "1.45rem",
    color: "#0F172A",
    lineHeight: 1.1
  },
  previewDesc: {
    color: "#64748B",
    fontSize: ".86rem",
    lineHeight: 1.55,
    marginTop: theme.spacing(1)
  },
  previewPrice: {
    fontWeight: 950,
    fontSize: "1.55rem",
    color: "#0066FF",
    marginTop: theme.spacing(1.2)
  },
  previewCta: {
    borderRadius: 18,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    width: "100%",
    padding: theme.spacing(1.25),
    fontWeight: 950,
    border: "none",
    marginTop: theme.spacing(1.5),
    cursor: "pointer",
    boxShadow: "0 12px 26px rgba(0,102,255,.24)"
  },
  itemCard: {
    borderRadius: 22,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    display: "grid",
    gridTemplateColumns: "72px 1fr auto",
    gap: theme.spacing(1.2),
    padding: theme.spacing(1.2),
    alignItems: "center",
    boxShadow: "0 10px 26px rgba(15,23,42,.045)",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "64px 1fr",
      "& $actions": { gridColumn: "1 / -1" }
    }
  },
  thumb: {
    width: 72,
    height: 72,
    borderRadius: 18,
    background: "linear-gradient(135deg,#EEF5FF,#FFFFFF)",
    border: "1px solid #E2E8F0",
    objectFit: "cover",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    color: "#0066FF",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: { width: 64, height: 64 }
  },
  itemName: { fontWeight: 950, color: "#0F172A", fontSize: ".95rem" },
  itemDesc: {
    color: "#64748B",
    fontSize: ".76rem",
    marginTop: 4,
    maxWidth: 520,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  itemMeta: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, alignItems: "center" },
  price: { fontWeight: 950, color: "#0066FF", fontSize: "1rem" },
  actions: { display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 12,
    border: "1px solid #E2E8F0",
    background: "#F8FAFC",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#334155"
  },
  sideCard: {
    borderRadius: 28,
    background: "#fff",
    border: "1px solid #E2E8F0",
    boxShadow: "0 18px 45px rgba(15,23,42,.06)",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  statsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing(1), marginTop: theme.spacing(1.2) },
  stat: { borderRadius: 18, padding: theme.spacing(1.2), background: "#F8FAFC", border: "1px solid #E2E8F0" },
  statValue: { fontWeight: 950, color: "#0F172A", fontSize: "1.4rem" },
  statLabel: { color: "#64748B", fontSize: ".72rem", fontWeight: 800, marginTop: 3 },
  aiBox: {
    marginTop: theme.spacing(1.2),
    borderRadius: 20,
    padding: theme.spacing(1.3),
    background: "linear-gradient(135deg,#EEF5FF,#FFFFFF)",
    border: "1px solid rgba(0,102,255,.14)"
  },
  dialogPaper: {
    borderRadius: 28,
    overflow: "hidden"
  },
  dialogTitle: {
    background: "linear-gradient(135deg,#0B2A7A,#0066FF,#00A3FF)",
    color: "#fff",
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  modalField: {
    "& .MuiOutlinedInput-root": { borderRadius: 16 }
  },
  imageUploadBox: {
    borderRadius: 24,
    minHeight: 190,
    border: "2px dashed rgba(0,102,255,.22)",
    background: "linear-gradient(135deg,#F8FAFC,#EEF5FF)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    cursor: "pointer",
    overflow: "hidden",
    position: "relative",
    transition: "all .15s",
    "&:hover": {
      borderColor: "#0066FF",
      boxShadow: "0 14px 30px rgba(0,102,255,.10)"
    }
  },
  imagePreview: {
    width: "100%",
    height: 220,
    objectFit: "cover",
    display: "block"
  },
  uploadHint: {
    fontWeight: 950,
    color: "#0066FF",
    fontSize: ".9rem"
  },
  uploadSub: {
    color: "#64748B",
    fontSize: ".72rem",
    marginTop: 5
  },
  miniAction: {
    borderRadius: 999,
    border: "1px solid rgba(0,102,255,.18)",
    background: "#fff",
    color: "#0066FF",
    fontWeight: 900,
    padding: "7px 12px",
    cursor: "pointer"
  }
}));

const emptyForm = {
  type: "product",
  name: "",
  subtitle: "",
  description: "",
  category: "",
  price: "",
  promoPrice: "",
  durationMinutes: "",
  stock: "",
  sku: "",
  imageUrl: "",
  externalUrl: "",
  ctaText: "Tenho interesse",
  tags: "",
  aiPrompt: "",
  featured: false,
  active: true
};

const CatalogModal = ({ open, onClose, item, reload }) => {
  const classes = useStyles();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (item) setForm({ ...emptyForm, ...item });
    else setForm(emptyForm);
  }, [item, open]);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const categoryOptions = [
    "Produtos",
    "Serviços",
    "Combos",
    "Promoções",
    "Mais vendidos",
    "Lançamentos",
    "Acessórios",
    "Premium",
    "Agendamento",
    "Orçamento"
  ];

  const handleUploadImage = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Envie apenas imagens.");
      return;
    }

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post("/catalog-items/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      set("imageUrl", data.imageUrl);
      toast.success("Imagem adicionada ao item.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do item.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        price: form.price || 0,
        promoPrice: form.promoPrice || null,
        durationMinutes: form.type === "service" ? form.durationMinutes || 30 : null,
        stock: form.type === "product" ? form.stock || 0 : null,
        tags: form.tags
          ? String(form.tags).split(",").map(t => t.trim()).filter(Boolean)
          : []
      };

      if (item?.id) await api.put(`/catalog-items/${item.id}`, payload);
      else await api.post("/catalog-items", payload);

      toast.success(item?.id ? "Item atualizado." : "Item criado.");
      reload();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialogTitle}>
        <div>
          <Typography style={{ fontWeight: 950, fontSize: "1.15rem" }}>
            {item?.id ? "Editar item do catálogo" : "Novo item do catálogo"}
          </Typography>
          <Typography style={{ fontSize: ".78rem", opacity: .85, marginTop: 4 }}>
            Cadastre produtos e serviços para a equipe e para a Korax IA.
          </Typography>
        </div>
        <button onClick={onClose} className={classes.iconBtn} style={{ background: "rgba(255,255,255,.16)", color: "#fff" }}>
          <Close fontSize="small" />
        </button>
      </div>

      <DialogContent style={{ padding: 20 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" fullWidth className={classes.modalField}>
              <InputLabel>Tipo</InputLabel>
              <Select value={form.type} label="Tipo" onChange={e => set("type", e.target.value)}>
                <MenuItem value="product">Produto</MenuItem>
                <MenuItem value="service">Serviço</MenuItem>
                <MenuItem value="combo">Combo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField className={classes.modalField} variant="outlined" label="Nome" fullWidth value={form.name} onChange={e => set("name", e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <TextField className={classes.modalField} variant="outlined" label="Subtítulo curto" fullWidth value={form.subtitle || ""} onChange={e => set("subtitle", e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              options={categoryOptions}
              value={form.category || ""}
              onChange={(e, value) => set("category", value || "")}
              onInputChange={(e, value) => set("category", value || "")}
              renderInput={params => (
                <TextField
                  {...params}
                  className={classes.modalField}
                  variant="outlined"
                  label="Categoria"
                  fullWidth
                  helperText="Selecione uma categoria ou digite uma nova"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField className={classes.modalField} variant="outlined" label="Preço" type="number" fullWidth value={form.price || ""} onChange={e => set("price", e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField className={classes.modalField} variant="outlined" label="Preço promocional" type="number" fullWidth value={form.promoPrice || ""} onChange={e => set("promoPrice", e.target.value)} />
          </Grid>

          {form.type !== "product" && (
            <Grid item xs={12} sm={4}>
              <TextField className={classes.modalField} variant="outlined" label="Duração em minutos" type="number" fullWidth value={form.durationMinutes || ""} onChange={e => set("durationMinutes", e.target.value)} />
            </Grid>
          )}

          {form.type === "product" && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField className={classes.modalField} variant="outlined" label="Estoque" type="number" fullWidth value={form.stock || ""} onChange={e => set("stock", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField className={classes.modalField} variant="outlined" label="SKU / Código" fullWidth value={form.sku || ""} onChange={e => set("sku", e.target.value)} />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={8}>
            <input
              id="catalog-image-upload"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleUploadImage}
            />

            <label htmlFor="catalog-image-upload" className={classes.imageUploadBox}>
              {form.imageUrl ? (
                <img src={form.imageUrl} alt="Preview" className={classes.imagePreview} />
              ) : (
                <div>
                  <div style={{ fontSize: 34, marginBottom: 8 }}>📸</div>
                  <div className={classes.uploadHint}>
                    {uploadingImage ? "Enviando imagem..." : "Adicionar foto do item"}
                  </div>
                  <div className={classes.uploadSub}>
                    Clique aqui para escolher da galeria ou câmera do celular
                  </div>
                </div>
              )}
            </label>

            {form.imageUrl && (
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <label htmlFor="catalog-image-upload" className={classes.miniAction}>
                  Trocar imagem
                </label>
                <button type="button" className={classes.miniAction} onClick={() => set("imageUrl", "")} style={{ color: "#EF4444" }}>
                  Remover
                </button>
              </div>
            )}
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField className={classes.modalField} variant="outlined" label="Texto do botão" fullWidth value={form.ctaText || ""} onChange={e => set("ctaText", e.target.value)} />
            <TextField
              className={classes.modalField}
              variant="outlined"
              label="URL manual da imagem"
              fullWidth
              value={form.imageUrl || ""}
              onChange={e => set("imageUrl", e.target.value)}
              style={{ marginTop: 12 }}
              helperText="Opcional: cole uma URL se não quiser enviar arquivo"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField className={classes.modalField} variant="outlined" label="Link externo / checkout / catálogo" fullWidth value={form.externalUrl || ""} onChange={e => set("externalUrl", e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <TextField className={classes.modalField} variant="outlined" label="Descrição" fullWidth multiline rows={3} value={form.description || ""} onChange={e => set("description", e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <TextField className={classes.modalField} variant="outlined" label="Tags separadas por vírgula" fullWidth value={form.tags || ""} onChange={e => set("tags", e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <TextField className={classes.modalField} variant="outlined" label="Instrução para IA" fullWidth multiline rows={2} value={form.aiPrompt || ""} onChange={e => set("aiPrompt", e.target.value)} placeholder="Ex: Ofereça este serviço quando o cliente perguntar por estética facial..." />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: 18 }}>
        <Button onClick={onClose} disabled={saving} style={{ borderRadius: 14, fontWeight: 900, textTransform: "none" }}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving} variant="contained" color="primary" className={classes.addBtn}>
          {saving ? "Salvando..." : item?.id ? "Salvar alterações" : "Criar item"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Catalog = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [searchParam, setSearchParam] = useState("");
  const [type, setType] = useState("");
  const [active, setActive] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [visualMode, setVisualMode] = useState("grid");

  const fetchItems = async () => {
    try {
      const { data } = await api.get("/catalog-items", {
        params: { searchParam, type, active }
      });
      setItems(data || []);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchSummary = async () => {
    try {
      const { data } = await api.get("/catalog-items/summary");
      setSummary(data || {});
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [searchParam, type, active]);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const eventName = `company${user.companyId}-catalog`;
    const onCatalog = () => {
      fetchItems();
      fetchSummary();
    };

    socket.on(eventName, onCatalog);
    return () => socket.off(eventName, onCatalog);
  }, [socket, user?.companyId]);

  const filteredCategories = useMemo(
    () => Array.from(new Set(items.map(i => i.category).filter(Boolean))).slice(0, 6),
    [items]
  );

  const openNew = () => {
    setSelectedItem(null);
    setModalOpen(true);
  };

  const openEdit = item => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const removeItem = async item => {
    if (!window.confirm(`Excluir "${item.name}"?`)) return;
    try {
      await api.delete(`/catalog-items/${item.id}`);
      toast.success("Item removido.");
      fetchItems();
      fetchSummary();
    } catch (err) {
      toastError(err);
    }
  };

  const publicCatalogIdentifier =
    summary?.catalogSlug ||
    user?.company?.catalogSlug ||
    user?.companyId;

  const publicCatalogUrl =
    `${window.location.origin}/catalogo/${publicCatalogIdentifier}`;

  const openPublicCatalog = () => {
    window.open(publicCatalogUrl, "_blank");
  };

  const copyPublicCatalog = async () => {
    try {
      await navigator.clipboard.writeText(publicCatalogUrl);
      toast.success("Link do catálogo copiado.");
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const parseTags = tags => {
    try {
      if (Array.isArray(tags)) return tags;
      if (!tags) return [];
      const parsed = JSON.parse(tags);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return String(tags).split(",").map(t => t.trim()).filter(Boolean);
    }
  };

  return (
    <div className={classes.pageRoot}>
      <CatalogWizardModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        item={selectedItem}
        categories={filteredCategories}
        reload={() => {
          fetchItems();
          fetchSummary();
        }}
      />

      <Dialog open={!!previewItem} onClose={() => setPreviewItem(null)} fullWidth maxWidth="sm" classes={{ paper: classes.previewShell }}>
        {previewItem && (
          <>
            <div className={classes.previewHero}>
              {previewItem.imageUrl ? (
                <img src={previewItem.imageUrl} alt={previewItem.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                previewItem.type === "service" ? "🧾" : previewItem.type === "combo" ? "🎁" : "📦"
              )}
              <span className={classes.productBadge}>
                {previewItem.type === "service" ? "Serviço" : previewItem.type === "combo" ? "Combo" : "Produto"}
              </span>
            </div>
            <div className={classes.previewContent}>
              <Typography className={classes.previewTitle}>{previewItem.name}</Typography>
              {previewItem.subtitle && (
                <Typography style={{ color: "#334155", fontWeight: 850, marginTop: 6 }}>
                  {previewItem.subtitle}
                </Typography>
              )}
              <Typography className={classes.previewDesc}>
                {previewItem.description || "Sem descrição cadastrada."}
              </Typography>
              <div className={classes.previewPrice}>
                {currency(previewItem.promoPrice || previewItem.price)}
              </div>
              <button className={classes.previewCta}>
                {previewItem.ctaText || "Tenho interesse"}
              </button>
            </div>
          </>
        )}
      </Dialog>

      <KoraxPageHeader
        title="Catálogo"
        subtitle="Cadastre produtos, serviços e combos para que a Korax utilize essas informações durante os atendimentos."
        actions={
          <Button
            className={classes.addBtn}
            startIcon={<Add />}
            onClick={openNew}
          >
            Novo item
          </Button>
        }
      />

      <div className={classes.layout}>
        <main className={classes.mainCard}>
          <div className={classes.tabs}>
            <button className={`${classes.tabBtn} ${type === "" ? classes.tabActive : ""}`} onClick={() => setType("")}>Tudo</button>
            <button className={`${classes.tabBtn} ${type === "product" ? classes.tabActive : ""}`} onClick={() => setType("product")}>Produtos</button>
            <button className={`${classes.tabBtn} ${type === "service" ? classes.tabActive : ""}`} onClick={() => setType("service")}>Serviços</button>
            <button className={`${classes.tabBtn} ${type === "combo" ? classes.tabActive : ""}`} onClick={() => setType("combo")}>Combos</button>
          </div>

          <div className={classes.toolbar}>
            <TextField className={classes.input} variant="outlined" size="small" placeholder="Buscar no catálogo..." value={searchParam} onChange={e => setSearchParam(e.target.value)} />
            <FormControl className={classes.input} variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select value={active} onChange={e => setActive(e.target.value)} label="Status">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Ativos</MenuItem>
                <MenuItem value="false">Inativos</MenuItem>
              </Select>
            </FormControl>
            <FormControl className={classes.input} variant="outlined" size="small">
              <InputLabel>Categoria</InputLabel>
              <Select value="" label="Categoria">
                <MenuItem value="">Todas</MenuItem>
                {filteredCategories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className={`${classes.tabBtn} ${visualMode === "grid" ? classes.tabActive : ""}`}
                onClick={() => setVisualMode("grid")}
                style={{ padding: "9px 12px" }}
              >
                Cards
              </button>
              <button
                className={`${classes.tabBtn} ${visualMode === "list" ? classes.tabActive : ""}`}
                onClick={() => setVisualMode("list")}
                style={{ padding: "9px 12px" }}
              >
                Lista
              </button>
            </div>
          </div>

          {visualMode === "grid" ? (
            <div className={classes.catalogGrid}>
              {items.map(item => (
                <article key={item.id} className={classes.productCard}>
                  <div className={classes.productImage}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} className={classes.productImageReal} />
                    ) : (
                      item.type === "service" ? "🧾" : item.type === "combo" ? "🎁" : "📦"
                    )}
                    <span className={classes.productBadge}>
                      {item.type === "service" ? "Serviço" : item.type === "combo" ? "Combo" : "Produto"}
                    </span>
                  </div>

                  <div className={classes.productBody}>
                    <Typography className={classes.productTitle}>{item.name}</Typography>
                    <Typography className={classes.productDescription}>
                      {item.subtitle || item.description || "Sem descrição cadastrada."}
                    </Typography>

                    <div className={classes.productFooter}>
                      <span className={classes.price}>{currency(item.promoPrice || item.price)}</span>
                      <Chip
                        size="small"
                        label={item.active ? "Ativo" : "Inativo"}
                        style={{
                          background: item.active ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)",
                          color: item.active ? "#16A34A" : "#EF4444",
                          fontWeight: 900
                        }}
                      />
                    </div>

                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                      {item.category && <Chip size="small" label={item.category} />}
                      {parseTags(item.tags).slice(0, 2).map(tag => <Chip key={tag} size="small" label={`#${tag}`} />)}
                    </div>

                    <div className={classes.cardActions}>
                      <button className={classes.cardActionBtn} onClick={() => setPreviewItem(item)}>
                        Preview
                      </button>
                      <button className={classes.cardActionBtn} onClick={() => openEdit(item)}>
                        Editar
                      </button>
                    </div>
                  </div>
                </article>
              ))}

              {!items.length && (
                <div style={{ padding: 30, textAlign: "center", color: "#64748B", fontWeight: 900, gridColumn: "1 / -1" }}>
                  Nenhum item cadastrado ainda. Clique em “Novo item” para começar.
                </div>
              )}
            </div>
          ) : (
            <div className={classes.itemsList}>
              {items.map(item => (
                <article key={item.id} className={classes.itemCard}>
                  <div className={classes.thumb}>
                    {item.imageUrl ? <img src={item.imageUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : item.type === "service" ? "🧾" : "📦"}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <Typography className={classes.itemName}>{item.name}</Typography>
                    <Typography className={classes.itemDesc}>
                      {item.subtitle || item.description || "Sem descrição"}
                    </Typography>

                    <div className={classes.itemMeta}>
                      <span className={classes.price}>{currency(item.promoPrice || item.price)}</span>
                      <Chip size="small" label={item.type === "service" ? "Serviço" : item.type === "combo" ? "Combo" : "Produto"} style={{ fontWeight: 900 }} />
                      {item.category && <Chip size="small" label={item.category} />}
                      <Chip size="small" label={item.active ? "Ativo" : "Inativo"} style={{ background: item.active ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)", color: item.active ? "#16A34A" : "#EF4444", fontWeight: 900 }} />
                      {parseTags(item.tags).slice(0, 2).map(tag => <Chip key={tag} size="small" label={`#${tag}`} />)}
                    </div>
                  </div>

                  <div className={classes.actions}>
                    <button className={classes.iconBtn} title="Preview" onClick={() => setPreviewItem(item)}><Visibility fontSize="small" /></button>
                    <button className={classes.iconBtn} title="Editar" onClick={() => openEdit(item)}><Edit fontSize="small" /></button>
                    {!item.serviceId && (
                      <button className={classes.iconBtn} title="Excluir" onClick={() => removeItem(item)} style={{ color: "#EF4444" }}><DeleteOutline fontSize="small" /></button>
                    )}
                  </div>
                </article>
              ))}

              {!items.length && (
                <div style={{ padding: 30, textAlign: "center", color: "#64748B", fontWeight: 900 }}>
                  Nenhum item cadastrado ainda. Clique em “Novo item” para começar.
                </div>
              )}
            </div>
          )}
        </main>

        <aside>
          <section className={classes.sideCard}>
            <Typography style={{ fontWeight: 950, color: "#0F172A" }}>
              Resumo do catálogo
            </Typography>

            <div className={classes.statsGrid}>
              <div className={classes.stat}>
                <div className={classes.statValue}>{summary.products || 0}</div>
                <div className={classes.statLabel}>Produtos</div>
              </div>
              <div className={classes.stat}>
                <div className={classes.statValue}>{summary.services || 0}</div>
                <div className={classes.statLabel}>Serviços</div>
              </div>
              <div className={classes.stat}>
                <div className={classes.statValue}>{summary.categories || 0}</div>
                <div className={classes.statLabel}>Categorias</div>
              </div>
              <div className={classes.stat}>
                <div className={classes.statValue}>{summary.active || 0}</div>
                <div className={classes.statLabel}>Ativos</div>
              </div>
            </div>
          </section>

          <section className={classes.sideCard}>
            <Typography style={{ fontWeight: 950, color: "#0F172A" }}>
              Catálogo público ✨
            </Typography>
            <Typography style={{ color: "#64748B", fontSize: ".78rem", marginTop: 8, lineHeight: 1.5 }}>
              Compartilhe este link no WhatsApp para o cliente final visualizar seus produtos, serviços e combos.
            </Typography>

            <div className={classes.aiBox}>
              <Typography style={{ fontWeight: 950, fontSize: ".78rem", color: "#0F172A" }}>
                Link público
              </Typography>
              <Typography style={{ color: "#0066FF", fontSize: ".72rem", marginTop: 6, wordBreak: "break-all", fontWeight: 900 }}>
                {publicCatalogUrl}
              </Typography>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={openPublicCatalog}
                  style={{ borderRadius: 14, fontWeight: 900, textTransform: "none" }}
                >
                  Abrir
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={copyPublicCatalog}
                  style={{ borderRadius: 14, fontWeight: 900, textTransform: "none" }}
                >
                  Copiar
                </Button>
              </div>
            </div>
          </section>

          <section className={classes.sideCard}>
            <Typography style={{ fontWeight: 950, color: "#0F172A" }}>
              Próximas funções
            </Typography>
            {["Link público do catálogo", "Envio pelo WhatsApp", "Recomendação automática", "Integração com Agenda"].map(t => (
              <div key={t} style={{ marginTop: 10, color: "#334155", fontWeight: 850, fontSize: ".78rem" }}>
                ✅ {t}
              </div>
            ))}
          </section>
        </aside>
      </div>
    </div>
  );
};

export default Catalog;
